import { useInAudit } from 'src/context/AuditContext'
import {
  Dashboard,
  DASHBOARD_CONTEXT,
  Filters,
  GetDashboardFilterValuesRequest,
  GetDashboardFilterValuesResponse,
  GetDashboardLayoutRequest,
  GetDashboardLayoutResponse,
  GetDashboardLiveCountsRequest,
  GetDashboardRequest,
  LiveChartCounts as LiveChartCountsProto,
  SaveDashboardLayoutRequest,
} from '@trustero/trustero-api-web/lib/dashboard/dashboard_pb'
import { DashboardServicePromiseClient } from '@trustero/trustero-api-web/lib/dashboard/dashboard_grpc_web_pb'
import { useLocation } from 'react-router-dom'
import { useCallback, useMemo } from 'react'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import queryString, { ParsedQuery } from 'query-string'
import { DashboardDateRangeDefaults } from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/DashboardFilterDropdowns/DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown.types'
import { TimeRangeConfig } from 'src/components/Reusable/Charts/Common/Charts.types'
import {
  DashboardGroupBy,
  LiveChartCounts,
} from 'src/Utils/dashboards/dashboards.types'
import { useCurrentAudit } from 'src/components/async/model/audit'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import {
  GrpcResponse,
  NewGrpcResponse,
} from '../../components/async/hooks/types'
import { useSwrImmutableGrpc } from '../../components/async/useSwrImmutableGrpc'
import { useSwrGrpc } from '../../components/async/useSwrGrpc'
import { daysToMillis } from '../globalHelpers'
import {
  applyDashboardFilters,
  getDashboardStepFromTimeBucket,
  getIsDashboardAuditSpecific,
} from './dashboards.helpers'
import { getSections, Section } from './dashboards.utils'

export const useGetTrustPortalDashboard = ({
  dname,
  filters,
  shouldFetch = false,
}: {
  dname: string
  filters: Filters
  shouldFetch?: boolean
}): GrpcResponse<Dashboard> => {
  return useGetDashboard({
    dname,
    filters,
    context: DASHBOARD_CONTEXT.TRUST_PORTAL,
    shouldFetch,
  })
}

export const useGetDashboard = ({
  dname,
  filters,
  context,
  shouldFetch = false,
}: {
  dname: string
  filters: Filters
  context?: DASHBOARD_CONTEXT
  shouldFetch?: boolean
}): GrpcResponse<Dashboard> => {
  const hasName = !!(dname && dname.length)
  const { auditId } = useInAudit()
  const request = new GetDashboardRequest()
    .setName(dname || '')
    .setIncludeData(true)
    .setFilters(filters)
  if (context) {
    request.setDashboardContext(context)
  }
  if (getIsDashboardAuditSpecific(dname)) {
    request.setAuditId(auditId || '')
  }
  const { response } = useSwrImmutableGrpc(
    DashboardServicePromiseClient.prototype.getDashboard,
    request,
    shouldFetch && hasName,
    { keepPreviousData: true },
  )

  return NewGrpcResponse(response)
}

export const useGetDashboardLiveCounts = ({
  dname,
  filters,
  chartNames,
  shouldFetch = false,
}: {
  dname: string
  filters: Filters
  chartNames?: string[]
  shouldFetch?: boolean
}): {
  data: Map<string, LiveChartCounts> | undefined
  loading: boolean
  isValidating: boolean
} => {
  const { auditId } = useInAudit()
  const request = new GetDashboardLiveCountsRequest()
    .setDashboardName(dname)
    .setFilters(filters)
  if (auditId) {
    request.setAuditId(auditId)
  }
  if (chartNames?.length) {
    request.setChartNamesList(chartNames)
  }
  const { response } = useSwrGrpc(
    DashboardServicePromiseClient.prototype.getDashboardLiveCounts,
    request,
    shouldFetch && !!(dname && dname.length),
    { keepPreviousData: true },
  )

  const data = useMemo(() => {
    if (!response.data) return undefined
    const result = new Map<string, LiveChartCounts>()
    response.data
      .getCountsMap()
      .forEach((counts: LiveChartCountsProto, chartName: string) => {
        const breakdown: Record<string, number> = {}
        counts.getBreakdownMap().forEach((count: number, key: string) => {
          breakdown[key] = count
        })
        result.set(chartName, { total: counts.getTotal(), breakdown })
      })
    return result
  }, [response.data])

  return {
    data,
    loading: !response.data && !response.error,
    isValidating: response.isValidating ?? false,
  }
}

export function useDashboardFilter(defaultDays = 31): Filters {
  const location = useLocation()
  const filters = useMemo(() => {
    const end = 0
    const start = Date.now() - 1000 * 60 * 60 * 24 * defaultDays
    const filters = new Filters() // Apply all filters for each parameter in URL
    filters.setStarttime(start)
    filters.setEndtime(end)
    const queryParams: ParsedQuery<string> = queryString.parse(
      location.search,
      {
        arrayFormat: 'bracket',
      },
    )
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyDashboardFilters(filters, queryParams, filterType),
    )
    return filters
  }, [location.search, defaultDays])
  return filters
}

// useOEDashboardSections returns the section list to render for an OE
// dashboard, filtered by the active filter-bar selections.
//
// - Framework mode (default): drops sections not in the dashboard_framework URL
//   param (when set) and not in the current audit's framework list.
// - Labels mode: drops sections whose combo doesn't contain any of the
//   dashboard_label URL-param values (when set); ignores audit-framework
//   scoping since label-combo sections aren't framework-named.
export const useOEDashboardSections = (
  dashboard: Dashboard | undefined,
  groupBy: DashboardGroupBy = DashboardGroupBy.FRAMEWORK,
): Section[] => {
  const location = useLocation()
  const { data: audit } = useCurrentAudit()
  const sections = useDashboardSections(dashboard)
  return useMemo(() => {
    if (!dashboard) {
      return []
    }
    const queryParams: ParsedQuery<string> = queryString.parse(
      location.search,
      { arrayFormat: 'bracket' },
    )

    if (groupBy === DashboardGroupBy.LABELS) {
      const labelFilter = queryParams[FilterParam.DASHBOARD_LABEL as string] as
        | string[]
        | undefined
      if (!labelFilter?.length) {
        return [...sections]
      }
      return sections.filter((section) => {
        const combo = section.getName().split(', ')
        return combo.some((label) => labelFilter.includes(label))
      })
    }

    const frameworkFilter = queryParams[
      FilterParam.DASHBOARD_FRAMEWORK as string
    ] as string[] | undefined
    const auditFrameworkNames =
      audit
        ?.getComplianceFrameworksList()
        .map((framework: ComplianceFramework) => framework.getName()) || []
    let filteredSections = [...sections]
    if (frameworkFilter?.length) {
      filteredSections = filteredSections.filter((section) =>
        frameworkFilter.includes(section.getName()),
      )
    }
    if (auditFrameworkNames.length) {
      filteredSections = filteredSections.filter((section) =>
        auditFrameworkNames.includes(section.getName()),
      )
    }
    return filteredSections
  }, [dashboard, sections, groupBy, location.search, audit])
}

export const useDashboardSections = (dashboard?: Dashboard): Section[] => {
  return useMemo(() => {
    if (!dashboard) {
      return []
    }
    return getSections(dashboard)
  }, [dashboard])
}

export const useGetURLParams = (): ParsedQuery<string> => {
  const location = useLocation()
  return queryString.parse(location.search)
}

export const useGetURLParam = <T>(
  name: string,
  validOptions: Set<T>,
  defaultValue: T,
): T => {
  const params = useGetURLParams()
  if (!params[name]) {
    return defaultValue
  }
  const param = Array.isArray(params[name])
    ? (params[name] as Array<unknown>)[0]
    : params[name]
  if (validOptions.has(param as T)) {
    return param as T
  }
  return defaultValue
}

const VALID_DASHBOARD_GROUP_BY = new Set<DashboardGroupBy>([
  DashboardGroupBy.FRAMEWORK,
  DashboardGroupBy.LABELS,
])

export const useDashboardGroupBy = (): DashboardGroupBy =>
  useGetURLParam<DashboardGroupBy>(
    FilterParam.DASHBOARD_GROUP_BY,
    VALID_DASHBOARD_GROUP_BY,
    DashboardGroupBy.FRAMEWORK,
  )

export const useTimeRangeConfigFromFilters = (
  timeBucket: string,
): TimeRangeConfig => {
  const location = useLocation()
  const queryParams: ParsedQuery<string> = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const filterParams = queryParams[FilterParam.DASHBOARD_DATE_RANGE]
  // use UTC time
  const today = new Date()
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  let minTimeStamp = now - daysToMillis(30)
  let maxTimeStamp = now
  if (filterParams && filterParams.length > 0) {
    switch (filterParams[0]) {
      case DashboardDateRangeDefaults.LAST_7_DAYS:
        minTimeStamp = now - daysToMillis(7)
        break
      case DashboardDateRangeDefaults.LAST_30_DAYS:
        minTimeStamp = now - daysToMillis(30)
        break
      case DashboardDateRangeDefaults.LAST_90_DAYS:
        minTimeStamp = now - daysToMillis(90)
        break
      case DashboardDateRangeDefaults.LAST_YEAR:
        minTimeStamp = now - daysToMillis(365)
        break
      default: {
        const dates = filterParams[0].split('-')
        const startMillis = parseInt(dates[0], 10)
        const endMillis = parseInt(dates[1], 10)
        minTimeStamp = startMillis
        maxTimeStamp = endMillis
        break
      }
    }
  }
  const step = useMemo(
    () => getDashboardStepFromTimeBucket(timeBucket),
    [timeBucket],
  )
  return useMemo(
    () => ({
      start: minTimeStamp,
      end: maxTimeStamp,
      step: step,
    }),
    [minTimeStamp, maxTimeStamp, step],
  )
}

export const useDashboardFilterValues = (
  dashboardContext: DASHBOARD_CONTEXT,
  dashboardName: string,
  dimension: string,
): GrpcResponse<GetDashboardFilterValuesResponse> => {
  const { response } = useSwrImmutableGrpc(
    DashboardServicePromiseClient.prototype.getDashboardFilterValues,
    new GetDashboardFilterValuesRequest()
      .setDashboardContext(dashboardContext)
      .setDashboardName(dashboardName)
      .setDimension(dimension),
    true,
  )
  return NewGrpcResponse(response)
}

export const useGetDashboardLayout = (
  dashboardName: string,
): GrpcResponse<GetDashboardLayoutResponse> => {
  const { response } = useSwrImmutableGrpc(
    DashboardServicePromiseClient.prototype.getDashboardLayout,
    new GetDashboardLayoutRequest().setDashboardName(dashboardName),
    !!dashboardName,
  )
  return NewGrpcResponse(response)
}

export const useSaveDashboardLayout = (): ((
  dashboardName: string,
  layoutJson: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(DashboardServicePromiseClient)
  return useCallback(
    async (dashboardName: string, layoutJson: string) => {
      const req = new SaveDashboardLayoutRequest()
        .setDashboardName(dashboardName)
        .setLayoutJson(layoutJson)
      await client.saveDashboardLayout(req)
    },
    [client],
  )
}
